import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import mouseAvatar from '../images/logos/ai-klikero-mouse-spinner.png';

const ThinkingStatus: React.FC = () => {
    const { t } = useTranslation();
    const [messageIndex, setMessageIndex] = useState(0);

    const messages = [
        t('thinkingMessages.analyzing', 'Analyzing your problem...'),
        t('thinkingMessages.scanning', 'Scanning for patterns...'),
        t('thinkingMessages.calculating', 'Calculating solution...'),
        t('thinkingMessages.finalizing', 'Finalizing explanation...'),
    ];

    useEffect(() => {
        const interval = setInterval(() => {
            setMessageIndex((prev: number) => (prev + 1) % messages.length);
        }, 3000);
        return () => clearInterval(interval);
    }, [messages.length]);

    return (
        <div className="flex flex-col items-center justify-center py-8">
            {/* Avatar Container */}
            <motion.div
                animate={{
                    y: [0, -10, 0],
                }}
                transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: "easeInOut"
                }}
                className="relative mb-6"
            >
                <div className="w-24 h-24 md:w-32 md:h-32 rounded-full bg-gradient-to-tr from-sky-400 to-indigo-500 p-1 shadow-lg overflow-hidden flex items-center justify-center">
                    <img
                        src={mouseAvatar}
                        alt="Thinking Avatar"
                        className="w-full h-full object-cover rounded-full"
                    />
                </div>

                {/* Thinking Bubbles */}
                <motion.div
                    initial={{ opacity: 0, scale: 0 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.5 }}
                    className="absolute -top-2 -right-2 w-6 h-6 bg-white rounded-full shadow-sm flex items-center justify-center"
                >
                    <div className="w-2 h-2 bg-sky-400 rounded-full animate-pulse" />
                </motion.div>
                <motion.div
                    initial={{ opacity: 0, scale: 0 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.7 }}
                    className="absolute -top-6 right-2 w-4 h-4 bg-white rounded-full shadow-sm flex items-center justify-center"
                >
                    <div className="w-1.5 h-1.5 bg-indigo-400 rounded-full animate-pulse" />
                </motion.div>
            </motion.div>

            {/* Thinking Text */}
            <div className="text-center">
                <div className="flex items-center justify-center space-x-1 mb-2">
                    <h2 className="text-xl md:text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-sky-600 to-indigo-600">
                        {t('thinkingMessages.thinking', 'Thinking')}
                    </h2>
                    <div className="flex space-x-1">
                        <motion.span
                            animate={{ opacity: [0, 1, 0] }}
                            transition={{ duration: 1.5, repeat: Infinity, times: [0, 0.3, 1] }}
                            className="text-xl md:text-2xl font-bold text-sky-600"
                        >.</motion.span>
                        <motion.span
                            animate={{ opacity: [0, 1, 0] }}
                            transition={{ duration: 1.5, repeat: Infinity, times: [0, 0.6, 1] }}
                            className="text-xl md:text-2xl font-bold text-sky-600"
                        >.</motion.span>
                        <motion.span
                            animate={{ opacity: [0, 1, 0] }}
                            transition={{ duration: 1.5, repeat: Infinity, times: [0, 0.9, 1] }}
                            className="text-xl md:text-2xl font-bold text-sky-600"
                        >.</motion.span>
                    </div>
                </div>

                <AnimatePresence mode="wait">
                    <motion.p
                        key={messageIndex}
                        initial={{ opacity: 0, y: 5 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -5 }}
                        className="text-slate-500 text-sm md:text-base h-6"
                    >
                        {messages[messageIndex]}
                    </motion.p>
                </AnimatePresence>
            </div>
        </div>
    );
};

export default ThinkingStatus;
